import {
  useMemo,
  useCallback,
  useState,
  useRef,
  Fragment,
  useEffect,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DynamicGrid from "../../../components/DynamicGrid/DynamicGrid";
import { incidentListCols } from "../analytics/dummyData/colums";
import { useIncidentList } from "./utils";
import { useFormattedDraftIncidents } from "../../../hooks/useFormattedDraftIncidents";
import { useStoreWrapper, useViewStateWrapper } from "../../../store/store";
import { shallow } from "zustand/shallow";
import useIncidentTypes from "../../../hooks/useIncidentTypes";
import { stages } from "../../../utils/helpers/incidentFormTabs";
import { buildIncidentOriginState } from "../../../utils/helpers/incidentNavigation";
import { IncidentStatus } from "../../../types/fieldInstance.interface";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import "./incidentList.css";
import moment from "moment";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface";

export const INCIDENT_GRID_KEY_PREFIX = "incident-list:";

// Transform incident data to match DynamicGrid expected format
const transformIncidentData = (incidents: any[], isDraft: boolean = false) => {
  return incidents.map((incident) => {
    // Handle both regular incidents and draft incidents field names
    const incidentName =
      incident.name || incident.Name || incident.IncidentName || "-";
    const incidentIdValue =
      incident.id ||
      incident.Id ||
      incident.incidentId ||
      incident.IncidentId;

    const facilityName = incident.FacilityName || incident.facility || "-";
    const incidentStatus = String(
      incident.Incident_Status__c || incident.status || "-"
    );

    // For drafts, extract date/time from Incident_Date_Time__c
    let facilityDate = "-";
    let facilityTime = "-";

    if (isDraft && incident.Incident_Date_Time__c) {
      const incidentDateTime = moment(incident.Incident_Date_Time__c);
      if (incidentDateTime.isValid()) {
        facilityDate = incidentDateTime.format("MM/DD/YYYY");
        facilityTime = incidentDateTime.format("HH:mm");
      }
    } else {
      // For non-draft incidents, use FacilityDate and FacilityTime directly
      facilityDate = incident.FacilityDate || "-";
      facilityTime = incident.FacilityTime || "-";
    }

    return {
      // Grid column data mapping - use exact API field names
      name: String(incidentName),
      type: String(
        incident.type ||
          incident.incidentType ||
          incident.Incident_Type__c ||
          "-"
      ),
      status: incidentStatus,
      facility: String(facilityName),
      facilityId: incident.Facility__c,
      resident: String(incident.Resident_Name__c || incident.resident || "-"),
      FacilityDate: facilityDate,
      FacilityTime: facilityTime,
      createdBy: String(
        incident.IncidentCreatedBy ||
          incident.createdBy ||
          incident.incidentCreatedBy ||
          "-"
      ),
      createdById: incident.Incident_Created_By__c,
      // Fields required for IncidentLinkCell component
      IncidentName: String(incidentName),
      incidentId: incidentIdValue,
      id: incidentIdValue,

      // Fields required for custom cells
      // (canMarkReady removed) grid-level mark-ready is handled on the incident detail page now
      // Pass the raw incident data for draft click handler
      ...(isDraft && { rawData: incident }),
      idempotencyKey: incident.idempotencyKey,
      stageSavedAt: incident.stageSavedAt,
      recordTypeId: incident.RecordTypeId,
    };
  });
};

const List = ({
  incidentTypeParam,
  facilityNameParam,
  startDateParam,
  endDateParam,
  allTabInitial = false,
  searchValue,
  resetFilters = false,
}: any) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { tableData, isLoading, debouncedSearchQuery } = useIncidentList(
    startDateParam,
    endDateParam
  );


  const gridColumnsDefault = useMemo(() => incidentListCols(), []);
  // Stop using backend-provided "canMarkReady" column in the grid.
  // We will render the Mark Ready action on the incident detail page next to Edit.
  const gridColumnsWithMarkReady = useMemo(() => gridColumnsDefault, [gridColumnsDefault]);
  const gridColumnsAll = useMemo(() => incidentListCols(true, false), []);

  // NOTE: marking an incident Ready for Approval is moved to the
  // incident detail action bar (next to Edit). The grid no longer
  // needs an onMarkReady handler.

  const { user, createForm, updateForm, setFromDraft } = useStoreWrapper(
    (state) => ({
      user: state.user,
      createForm: state.createForm,
      updateForm: state.updateForm,
      setFromDraft: state.setFromDraft,
    }),
    shallow
  );

  const { incidentTypes } = useIncidentTypes();

  // Use custom hook to get formatted draft incidents
  const { formattedDrafts, isLoading: draftsLoading } =
    useFormattedDraftIncidents({
      debouncedSearchQuery,
    });

  // Draft click handler - same logic as the old Draft.tsx
  const handleDraftClick = useCallback(
    (item: any) => {
      const incidentType = incidentTypes.find(
        (incident: any) => incident.flag === item.RecordTypeId
      );
      const incidentTypeId = incidentType ? incidentType.id : "";
      const savedStage = stages.find(
        (stage) => stage.label === item.stageSavedAt
      );

      createForm(item.Id);
      updateForm(item.Id, {
        type: "UPDATE_FORM_STATE",
        payload: { incidentStatus: IncidentStatus.Draft },
      });
      updateForm(item.Id, {
        type: "UPDATE_BASE_FORM_DATA",
        payload: {
          incidentType: incidentTypeId,
          idempotencyKey: item.idempotencyKey,
          facilityId: item?.Facility__c,
        },
      });
      setFromDraft(true);
      useViewStateWrapper.getState().markLeavingForIncident();
      const isOwner = item.Incident_Created_By__c === user.userId;
      const path = `/incidents/${item.Id}/${
        savedStage?.path ?? stages[0].path
      }?mode=${isOwner ? "edit" : "view"}`;
      navigate(path, { state: buildIncidentOriginState(location) });
    },
    [incidentTypes, createForm, updateForm, setFromDraft, user.userId, navigate, location]
  );

  // Restore the tab the user left on, but only for the same URL (dashboard links default to All).
  const [restoredTab] = useState<number | null>(() => {
    const tab = useViewStateWrapper.getState().incidentListTab;
    return tab && tab.path === location.pathname + location.search
      ? tab.index
      : null;
  });
  const keepRestoredTabRef = useRef(restoredTab !== null);
  // allTabInitial flips from mount effects; only those should be ignored, not later typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      keepRestoredTabRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  const [selected, setSelected] = useState<number>(
    restoredTab ?? (allTabInitial ? 5 : 1)
  );
  const handleSelect = (e: TabStripSelectEventArguments) => {
    keepRestoredTabRef.current = false;
    setSelected(e.selected);
    useViewStateWrapper
      .getState()
      .setIncidentListTab({
        index: e.selected,
        path: location.pathname + location.search,
      });
  };

  // Switch to the "All" tab when allTabInitial becomes true after mount
  useEffect(() => {
    if (keepRestoredTabRef.current) return;
    setSelected(allTabInitial ? 5 : 1);
  }, [allTabInitial]);

  const initialIncidentTypeFilter = useMemo(() => {
    if (incidentTypeParam) {
      return {
        field: "type",
        operator: "eq",
        value: incidentTypeParam,
      };
    }
  }, [incidentTypeParam]);

  const initialFacilityFilter = useMemo(() => {
    if (facilityNameParam) {
      return {
        field: "facility",
        operator: "eq",
        value: facilityNameParam,
      };
    }
  }, [facilityNameParam]);

  // Memoize search filter to avoid recreating on every search
  const searchFilter = useMemo(() => {
    if (!searchValue || searchValue.length === 0) return null;
    
    return {
      logic: "or",
      filters: gridColumnsDefault.map((col: any) => ({
        field: col.accessor,
        operator: "contains",
        value: searchValue,
      })),
    } as CompositeFilterDescriptor;
  }, [searchValue, gridColumnsDefault]);

  const initialFilterArray = useMemo(() => {
    // Return empty array when resetFilters is true to clear all filters
    if (resetFilters) {
      return [];
    }
    
    let filterArray: any[] = [];
    if (initialIncidentTypeFilter) {
      filterArray.push(initialIncidentTypeFilter);
    }
    if (initialFacilityFilter) {
      filterArray.push(initialFacilityFilter);
    }
    if (searchFilter) {
      filterArray.push(searchFilter);
    }
    // Remove automatic tab switching - let users navigate freely
    // The original logic forced users to the "All" tab when filters were applied
    // but this prevented navigation to other tabs when filters were active
    return filterArray;
  }, [
    resetFilters,
    initialIncidentTypeFilter,
    initialFacilityFilter,
    searchFilter,
  ]);

  const groupedIncidentData = useMemo(() => {
    if (!tableData?.incidents) {
      return {
        openSubmitted: [],
        signatureInProgress: [],
        completed: [],
        deleted: [],
        readyForApproval: [],
        draft: [],
        all: [],
      };
    }

    const incidents = tableData.incidents.filter((incident: any) => incident.FacilityDateTime !== null);
    
    // Single pass grouping for better performance with many facilities
    const groups = {
      openSubmitted: [],
      signatureInProgress: [],
      completed: [],
      deleted: [],
      readyForApproval: [],
    } as any;

    for (const incident of incidents) {
      const status = incident.Incident_Status__c || incident.status;
      
      switch (status) {
        case "Open":
        case "Submitted":
          groups.openSubmitted.push(incident);
          break;
        case "Signature in Progress":
          groups.signatureInProgress.push(incident);
          break;
        case "Completed":
          groups.completed.push(incident);
          break;
        case "Deleted":
          groups.deleted.push(incident);
          break;
        case "Ready for Approval":
          groups.readyForApproval.push(incident);
          break;
      }
    }

    return {
      ...groups,
      draft: formattedDrafts,
      all: incidents.concat(formattedDrafts),
    };
  }, [tableData, formattedDrafts]);


  // Memoize transformed data to prevent recalculation on collapse/expand
  const transformedDrafts = useMemo(
    () => transformIncidentData(groupedIncidentData.draft, true),
    [groupedIncidentData.draft]
  );
  const transformedOpenSubmitted = useMemo(
    () =>
      transformIncidentData([
        ...groupedIncidentData.openSubmitted,
        ...groupedIncidentData.readyForApproval,
      ]),
    [
      groupedIncidentData.openSubmitted,
      groupedIncidentData.readyForApproval,
    ]
  );
  const transformedSignatureInProgress = useMemo(
    () => transformIncidentData(groupedIncidentData.signatureInProgress),
    [groupedIncidentData.signatureInProgress]
  );
  const transformedCompleted = useMemo(
    () => transformIncidentData(groupedIncidentData.completed),
    [groupedIncidentData.completed]
  );
  const transformedDeleted = useMemo(
    () => transformIncidentData(groupedIncidentData.deleted),
    [groupedIncidentData.deleted]
  );
  const transformedAll = useMemo(
    () => {
      const nonDeletedAll = groupedIncidentData.all.filter((inc: any) => inc.status !== "Deleted");
      return transformIncidentData(nonDeletedAll);
    },
    [groupedIncidentData.all]
  );

  // Lazy render function to prevent all tabs from rendering simultaneously
  const renderTabContent = useCallback((tabIndex: number) => {
    if (selected !== tabIndex) {
      return <div style={{ minHeight: 200 }}>Loading...</div>;
    }

    // Enable virtualization for tabs with potentially large datasets
    const enableVirtualization = (data: any[]) => data.length > 100;

    switch (tabIndex) {
      case 0:
        return (
          <DynamicGrid
            key={`draft-incidents-${JSON.stringify(initialFilterArray)}`}
            viewStateKey={`${INCIDENT_GRID_KEY_PREFIX}0`}
            gridCols={gridColumnsDefault}
            gridData={transformedDrafts}
            showTitleBar={true}
            showSearchBar={false}
            barTitle="Draft Incidents"
            reportName="Incident List"
            isLoading={draftsLoading}
            collapsible={false}
            onRowClick={handleDraftClick}
            startDate={startDateParam}
            endDate={endDateParam}
            initialFilter={initialFilterArray}
            isActive={true}
            enableVirtualization={enableVirtualization(transformedDrafts)}
            virtualRowHeight={45}
          />
        );
      case 1:
        return (
          <DynamicGrid
            key={`open-submitted-incidents-${JSON.stringify(initialFilterArray)}`}
            viewStateKey={`${INCIDENT_GRID_KEY_PREFIX}1`}
            gridCols={gridColumnsWithMarkReady}
            gridData={transformedOpenSubmitted}
            showTitleBar={true}
            showSearchBar={false}
            barTitle="Open & Submitted Incidents"
            reportName="Incident List"
            isLoading={isLoading}
            collapsible={false}
            startDate={startDateParam}
            endDate={endDateParam}
            initialFilter={initialFilterArray}
            isActive={true}
            enableVirtualization={enableVirtualization(transformedOpenSubmitted)}
            virtualRowHeight={45}
          />
        );
      case 2:
        return (
          <DynamicGrid
            key={`signature-progress-incidents-${JSON.stringify(initialFilterArray)}`}
            viewStateKey={`${INCIDENT_GRID_KEY_PREFIX}2`}
            gridCols={gridColumnsDefault}
            gridData={transformedSignatureInProgress}
            showTitleBar={true}
            showSearchBar={false}
            barTitle="Signature in Progress Incidents"
            reportName="Incident List"
            isLoading={isLoading}
            collapsible={false}
            startDate={startDateParam}
            endDate={endDateParam}
            initialFilter={initialFilterArray}
            isActive={true}
            enableVirtualization={enableVirtualization(transformedSignatureInProgress)}
            virtualRowHeight={45}
          />
        );
      case 3:
        return (
          <DynamicGrid
            key={`completed-incidents-${JSON.stringify(initialFilterArray)}`}
            viewStateKey={`${INCIDENT_GRID_KEY_PREFIX}3`}
            gridCols={gridColumnsDefault}
            gridData={transformedCompleted}
            showTitleBar={true}
            showSearchBar={false}
            barTitle="Completed Incidents"
            reportName="Incident List"
            isLoading={isLoading}
            collapsible={false}
            startDate={startDateParam}
            endDate={endDateParam}
            initialFilter={initialFilterArray}
            isActive={true}
            enableVirtualization={enableVirtualization(transformedCompleted)}
            virtualRowHeight={45}
          />
        );
      case 4:
        return (
          <DynamicGrid
            key={`deleted-incidents-${JSON.stringify(initialFilterArray)}`}
            viewStateKey={`${INCIDENT_GRID_KEY_PREFIX}4`}
            gridCols={gridColumnsDefault}
            gridData={transformedDeleted}
            showTitleBar={true}
            showSearchBar={false}
            barTitle="Deleted Incidents"
            reportName="Incident List"
            isLoading={isLoading}
            collapsible={false}
            startDate={startDateParam}
            endDate={endDateParam}
            initialFilter={initialFilterArray}
            isActive={true}
            enableVirtualization={enableVirtualization(transformedDeleted)}
            virtualRowHeight={45}
          />
        );
      case 5:
        return (
          <DynamicGrid
            key={`all-incidents-${JSON.stringify(initialFilterArray)}`}
            viewStateKey={`${INCIDENT_GRID_KEY_PREFIX}5`}
            gridCols={gridColumnsAll}
            gridData={transformedAll}
            showTitleBar={true}
            showSearchBar={false}
            barTitle="All Incidents"
            reportName="Incident List"
            isLoading={isLoading}
            collapsible={false}
            startDate={startDateParam}
            endDate={endDateParam}
            initialFilter={initialFilterArray}
            isActive={true}
            enableVirtualization={enableVirtualization(transformedAll)}
            virtualRowHeight={45}
          />
        );
      default:
        return null;
    }
  }, [selected, gridColumnsDefault, gridColumnsWithMarkReady, gridColumnsAll, transformedDrafts, transformedOpenSubmitted, transformedSignatureInProgress, transformedCompleted, transformedDeleted, transformedAll, draftsLoading, isLoading, handleDraftClick, startDateParam, endDateParam, initialFilterArray]);

  return (
    <Fragment>
      <TabStrip selected={selected} onSelect={handleSelect} size="large">
        <TabStripTab title={<span style={{ margin: "0 15px" }}>Drafts</span>}>
          {renderTabContent(0)}
        </TabStripTab>
        <TabStripTab
          title={<span style={{ margin: "0 15px" }}>Open | Submitted | Ready For Approval</span>}
        >
          {renderTabContent(1)}
        </TabStripTab>
        <TabStripTab
          title={<span style={{ margin: "0 15px" }}>Signature in Progress</span>}
        >
          {renderTabContent(2)}
        </TabStripTab>
        <TabStripTab
          title={<span style={{ margin: "0 15px" }}>Completed</span>}
        >
          {renderTabContent(3)}
        </TabStripTab>
        <TabStripTab title={<span style={{ margin: "0 15px" }}>Deleted</span>}>
          {renderTabContent(4)}
        </TabStripTab>
        <TabStripTab title={<span style={{ margin: "0 15px" }}>All</span>}>
          {renderTabContent(5)}
        </TabStripTab>
      </TabStrip>
    </Fragment>
  );
};

export default List;
